<template>
  <div class="overlay overlay--grow" v-show="modalVisible"> 
    <transition name="slideDown">

      <div>
        <div ref="target" class="overlay__inner" v-show="modalVisible">
          <div class="pricing-row-config">
            <button class="btn btn-icon pricing-row-config__close" aria-label="close"
              @click.prevent="toggleModal"
            ><i class="fa fa-close"></i></button>
            <h3 class="pricing-row-config__head pricing-row-config__head--left pricing-row-config__head--main pricing-row-config__head--drk"
              @mouseenter="moveable.draggable = true"
              @mouseleave="moveable.draggable = false"
            >{{ componentName }} Price Breakdown &amp; Description</h3>  
            <div>
              <table cellpadding="0" cellspacing="0" class="pricing-table__table">
                <thead class="pricing-table__table-head">
                  <tr class="pricing-table__row">
                    <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--xl pricing-table__cell--head-label align-l" style="min-width: 340px;"><span style="white-space: nowrap;">Breakdown - {{ componentName }} Price  <span>(for {{ date }})</span></span></th>
                    <th class="pricing-table__cell pricing-table__cell--head">Best Estimate</th>
                    <th class="pricing-table__cell pricing-table__cell--head">High Estimate</th>
                    <th class="pricing-table__cell pricing-table__cell--head">Low Estimate</th>
                  </tr>
                </thead>
                <tbody class="pricing-table__table-body">
                  <template v-if="['premium', 'premium-legacy-1'].includes(utilityPlan.slug) && subComponents">
                    <sub-component 
                      v-for="(subComponent, index) in subComponents"
                      :sub-component-name="subComponent.name"
                      :sub-component-id="subComponent.id"
                      :request-id="requestId"
                      :term="term"
                      :term-usage="termUsage"
                      :date="date"
                      :key="index"
                      :modal-visible="modalVisible"
                    ></sub-component>
                   </template> 

                  <template v-if="! ['premium', 'premium-legacy-1'].includes(utilityPlan.slug) && subComponents">
                    <tr class="pricing-table__row" v-for="(subComponent, index) in subComponents"> 
                      <td class="pricing-table__cell pricing-table__cell--xl">{{ subComponent.name }}</td>
                      <td class="pricing-table__cell align-cntr" colspan="3"><span v-if="index + 1 ===  Math.ceil(subComponents.length / 2)"><a :href="upgradeLink" @click.prevent="loadAccountView">Subscription required</a> for this utility:<br> {{ utility }}</span></td>                   
                    </tr>
                  </template>

                  <template v-if="brokerFee">
                    <tr class="pricing-table__row">
                      <td class="pricing-table__cell pricing-table__cell--xl">Adjustment</td>
                      <td class="pricing-table__cell align-cntr">{{ formatCurrencyValue(brokerFee, 4) }}</td>
                      <td class="pricing-table__cell align-cntr">{{ formatCurrencyValue(brokerFee, 4) }}</td>
                      <td class="pricing-table__cell align-cntr">{{ formatCurrencyValue(brokerFee, 4) }}</td>
                    </tr>
                  </template>
                </tbody>
                <tfoot class="pricing-table__table-footer">
                  <tr class="pricing-table__row">
                    <td class="pricing-table__cell pricing-table__cell--xl"><b>{{ componentName }} Total ($/kWh):</b></td>
                    <td class="pricing-table__cell align-cntr"><b>{{ formatCurrencyValue(best, 4) }}</b></td>
                    <td class="pricing-table__cell align-cntr"><b>{{ formatCurrencyValue(high, 4) }}</b></td>
                    <td class="pricing-table__cell align-cntr"><b>{{ formatCurrencyValue(low, 4) }}</b></td>
                  </tr>
                </tfoot>
              </table>
              <button class="btn btn-sm btn-tertiary btn-icon mrgn-0 pad-left-0 mrgn-top-10" @click="showDescription = !showDescription"><i class="fa" :class="{ 'fa-plus' :  !showDescription, 'fa-minus' : showDescription }"></i>Description - {{ componentName }} Price</button>
              <div class="pricing-row-config__detail" v-show="showDescription">
                <p>{{ componentDescription }}</p>
              </div>
            </div>
          </div>
        </div>

        <Moveable
          className="moveable"
          :target="$refs.target"
          :draggable="moveable.draggable"
          :origin="false"
          :hideDefaultLines="true"
          @drag="handleDrag"
        />
      </div>

    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import Moveable from 'vue3-moveable'
import moment from 'moment'
import SubComponent from './SubComponentRow.vue'
import { formatCurrency } from '../../../../common/helpers'

export default {
  props: ['modalVisible', 'componentId', 'requestId',  'date', 'term', 'termUsage', 'brokerFee'],
  components: {
    Moveable,
    'sub-component': SubComponent,
  },
  data() {
    return {
      fullData: [],
      showDescription: false,
      moveable: {
        draggable: false
      },
    }
  },
  computed: {
    pricingComponents() {
      return this.$store.getters.dashboardPricingComponents
    },
    utilityPlan() {
      return this.$store.getters.utilityPlan
    },
    utility() {
      return this.$store.getters.utility.name
    },
    upgradeLink() {
      switch (this.utilityPlan.slug) {
        case 'free':
          return '/subscribe'
        default:
          return '/account/subscription'
      }
    },
    contextualData() {
      if (this.fullData.length && this.date) {
        return this.fullData.filter(item => moment(item.date).format('M/DD/YYYY') === this.date)[0]
      }
    },
    best() {
      if (this.contextualData && this.contextualData.avg) {
        return (this.contextualData.avg  / this.termUsage) + (this.brokerFee * 1)
      }
    },
    high() {
      if (this.contextualData && this.contextualData.high) {
        return (this.contextualData.high / this.termUsage) + (this.brokerFee * 1)
      }
    },
    low() {
      if (this.contextualData && this.contextualData.low) {
        return (this.contextualData.low / this.termUsage) + (this.brokerFee * 1)
      }
    },
    activeComponentName() {
      if (this.componentId) {
        return this.pricingComponentName(this.componentId * 1)
      }
    },
    currentComponent() {
      return this.pricingComponents.filter(item => item.id == this.componentId)[0]
    },
    componentName() {
      if (this.currentComponent) {
        return this.currentComponent.name
      }
    },
    componentDescription() {
      if (this.currentComponent) {
        return this.currentComponent.description
      }
    },
    subComponents() {
      if (this.currentComponent) {
        return this.currentComponent.children
      }
    }
  },
  methods: {
    handleDrag({ target, transform }) {
      let ogTransform = target.style.transform

      target.style.transform = transform

      let bounding = target.getBoundingClientRect()

      if (bounding.top < 0) {
        target.style.transform = ogTransform
      }
    },
    pricingComponentName(componentId) {
      if (componentId === -1) {
        return 'Total'
      }

      let component = this.pricingComponents.filter(component => component.id === componentId)
      if (component.length) {
        return component[0].name
      } 
    },
    toggleModal() {
      document.querySelectorAll('.moveable').forEach(moveable => {
        moveable.style.transform = "matrix(1,0,0,1,0,0) translate(0, 0)')"
      })

      this.showDescription = false
      this.$emit('toggleModal')
    },
    populateModal() {
      if (this.requestId && this.componentId) {
        axios.get('/pricing-calculator/data/requests/' + this.requestId + '/components/' + this.componentId)
          .then(response => {
            this.fullData = response.data[this.term]
          })
      }
    },
    loadAccountView() {
      window.location.href = this.upgradeLink
    },
    formatCurrencyValue(val, decimals) {
      return formatCurrency(val, decimals)
    }
  },
  created() {
    this.populateModal()
  },
  mounted() {
    this.populateModal()
  },
  watch: {
    modalVisible(bool) {
      if (bool) {
        this.populateModal()
      }
    }
  }
}
</script>
